/**
 * Display-only breakdown for the View ML agent modal.
 * Does not change remittance, settlement, or cashbook amounts.
 *
 * Receiving agent keeps unloading (Paid + To-Pay). Return to company =
 * To-Pay collected − unloading kept. Loading stays on booking branch.
 */

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

const modeOf = (shipment) =>
  String(shipment?.mode_of_payment || shipment?.payment_mode || '')
    .trim()
    .toLowerCase();

const statusOf = (shipment) => String(shipment?.status || '').trim().toLowerCase();

const isToPay = (mode) => mode === 'to_pay' || mode === 'topay';
const isCredit = (mode) => mode === 'credit' || mode === 'on_account' || mode === 'onaccount';
const isDelivered = (status) => status === 'delivered';

/** Same face-value rule as backend To-Pay obligation (before own-hub deferral). */
export const toPayGcFace = (shipment) => {
  const paid = round2(shipment?.paid_amount);
  const balance = round2(shipment?.balance_amount);
  const obligation = round2(paid + balance);
  if (obligation > 0) return obligation;
  return round2(shipment?.total_amount || shipment?.freight_charges);
};

/**
 * @param {object|null} collection agent_collections row
 * @param {Array} shipments ML included shipments
 */
export function buildAgentMlModalLedger(collection, shipments) {
  const ids = new Set((collection?.shipment_ids || []).map((id) => String(id)));
  const rows = (shipments || []).filter((s) => ids.has(String(s.shipment_id)));
  const labourKeepSeparate =
    collection?.ml_commission_remit_mode === 'labour_keep_separate'
    || collection?.deduct_commission_from_remit === false;

  let gcTotal = 0;
  let freight = 0;
  let toPayFace = 0;
  let paidTotal = 0;
  let creditTotal = 0;
  let toPayDeliveredFace = 0;
  let unloadingKept = 0;
  let unloadingOnToPayDelivered = 0;

  rows.forEach((s) => {
    gcTotal = round2(gcTotal + round2(s.total_amount));
    freight = round2(freight + round2(s.freight_charges));
    const mode = modeOf(s);
    const unload = round2(s.unloading_charges);
    if (isToPay(mode)) {
      // Option 2: To-Pay face = customer GC amount (paid+balance / total), not freight.
      const face = toPayGcFace(s);
      toPayFace = round2(toPayFace + face);
      if (isDelivered(statusOf(s))) {
        toPayDeliveredFace = round2(toPayDeliveredFace + face);
        unloadingOnToPayDelivered = round2(unloadingOnToPayDelivered + unload);
      }
      unloadingKept = round2(unloadingKept + unload);
    } else if (isCredit(mode)) {
      creditTotal = round2(creditTotal + round2(s.total_amount || s.freight_charges));
    } else {
      paidTotal = round2(paidTotal + round2(s.total_amount));
      unloadingKept = round2(unloadingKept + unload);
    }
  });

  const collectionUnload = round2(collection?.unloading_kept);
  if (collectionUnload > unloadingKept) {
    unloadingKept = collectionUnload;
  }

  const remitToPay = round2(
    collection?.to_pay_freight ?? collection?.gross_to_pay,
  );

  const heldUntilDelivered = round2(Math.max(0, toPayFace - toPayDeliveredFace));
  const ownHubHoldingToPay =
    heldUntilDelivered > 0.009 && remitToPay + 0.009 < toPayFace;

  let dueLabel = 'Due now';
  if (!ownHubHoldingToPay && labourKeepSeparate) {
    dueLabel = Number(collection?.commission_amount || 0) > 0
      ? 'To-Pay − commission − unloading kept'
      : 'To-Pay − unloading kept';
  } else if (!ownHubHoldingToPay && Number(collection?.commission_amount || 0) > 0) {
    dueLabel = collection?.tds_amount
      ? 'Net to remit (To-Pay − net commission)'
      : 'Net to remit (To-Pay − commission)';
  }

  // Same net as Settle with Company: To-Pay − unloading kept (receiving keeps unload).
  const netAfterUnloadKept = round2(Math.max(0, toPayFace - unloadingKept));
  const toPayHeld = ownHubHoldingToPay ? netAfterUnloadKept : 0;
  // Remit = To-Pay collected − unloading kept on those GCs (receiving keeps unload).
  const toPayRemitNow = ownHubHoldingToPay
    ? 0
    : round2(Math.max(0, toPayDeliveredFace - unloadingOnToPayDelivered));
  // Prefer backend amount_due after delivery (option 1/2 remittance engine).
  const collectionDue = round2(
    collection?.amount_outstanding ?? collection?.amount_due ?? toPayRemitNow,
  );
  const returnToCompany = ownHubHoldingToPay
    ? netAfterUnloadKept
    : round2(collectionDue > 0.009 ? collectionDue : toPayRemitNow);

  const remitBreakdown = [];
  // Option 1 with commission: hide unload breakdown (commission is the main net).
  // Option 2: always show unload keep + remit.
  const commissionNet =
    !labourKeepSeparate
    && Number(collection?.commission_amount || 0) > 0
    && !ownHubHoldingToPay;
  if (rows.length && !commissionNet) {
    if (unloadingKept > 0.009) {
      remitBreakdown.push({
        label: 'Unloading kept (receiving agent)',
        value: unloadingKept,
      });
    }
    if (toPayHeld > 0.009) {
      remitBreakdown.push({
        label: 'To-Pay (after delivery)',
        value: toPayHeld,
      });
    } else if (toPayRemitNow > 0.009 || returnToCompany > 0.009) {
      remitBreakdown.push({
        label: 'To-Pay to company (after unload kept)',
        value: returnToCompany,
      });
    }
  }

  if (unloadingKept > 0.009 && returnToCompany < 0.01 && !ownHubHoldingToPay) {
    dueLabel = 'Unloading kept — nothing to remit';
  } else if (!ownHubHoldingToPay && unloadingKept > 0.009 && labourKeepSeparate) {
    dueLabel = Number(collection?.commission_amount || 0) > 0
      ? 'To-Pay − commission − unloading kept'
      : 'To-Pay − unloading kept';
  } else if (!ownHubHoldingToPay && unloadingKept > 0.009 && !commissionNet) {
    dueLabel = 'To-Pay − unloading kept';
  }

  return {
    gcTotal: rows.length ? gcTotal : null,
    freight: rows.length ? freight : round2(collection?.total_freight),
    toPayFace: rows.length ? toPayFace : round2(collection?.to_pay_freight ?? collection?.gross_to_pay),
    toPayHeld,
    paidTotal,
    creditTotal,
    dueNow: returnToCompany,
    outstanding: returnToCompany,
    dueLabel,
    returnToCompany,
    unloadingReceiving: unloadingKept,
    otherRemit: 0,
    remitBreakdown,
    commissionSeparate: labourKeepSeparate,
    note: labourKeepSeparate
      ? 'He keeps his commission and the unloading, and returns the rest.'
      : '',
    shipmentCount: ids.size || rows.length || 0,
    showGcBreakdown: rows.length > 0,
  };
}
